import { ErrorHandlerProvider, ErrorBoundary } from '@sixfold/app-utils';
import { ThemeProvider, ThemeObject, NotificationBoundary, ToastProvider } from '@sixfold/common-ui';
import {
  transporeonLightClassName,
  transporeonDarkClassName,
  trimbleLightClassName,
  trimbleDarkClassName,
} from '@sixfold/common-ui/dist/lib/css';
import transporeonDark from '@sixfold/design-tokens/build/transporeon/json/variables-dark.json';
import transporeonLight from '@sixfold/design-tokens/build/transporeon/json/variables.json';
import trimbleDark from '@sixfold/design-tokens/build/trimble/json/variables-dark.json';
import trimbleLight from '@sixfold/design-tokens/build/trimble/json/variables.json';
import iconsUrl from '@sixfold/icons/dist/sprite.svg';
import React, { useCallback, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';

import { App } from './app';
import { ErrorPlaceholder } from './components/error_placeholder';
import { getAuthService } from './lib/authorization';
import { getEmbedConfig } from './lib/data';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@xyflow/react/dist/style.css';
import '@sixfold/common-ui/dist/lib/styles/global.css';
import '@sixfold/design-tokens/build/transporeon/css/fonts.css';
import '@sixfold/design-tokens/build/transporeon/css/variables.css';
import '@sixfold/design-tokens/build/transporeon/css/variables-dark.css';
import '@sixfold/design-tokens/build/trimble/css/fonts.css';
import '@sixfold/design-tokens/build/trimble/css/variables.css';
import '@sixfold/design-tokens/build/trimble/css/variables-dark.css';
import './app.css';

const THEME_STORAGE_KEY = 'common-ui-theme-v2';
const DEFAULT_THEME = 'trimble-light';

const themes: Record<string, ThemeObject> = {
  'transporeon-light': {
    className: transporeonLightClassName,
    variables: transporeonLight,
  },
  'transporeon-dark': {
    className: transporeonDarkClassName,
    variables: transporeonDark,
  },
  'trimble-light': {
    className: trimbleLightClassName,
    variables: trimbleLight,
  },
  'trimble-dark': {
    className: trimbleDarkClassName,
    variables: trimbleDark,
  },
};

const getStoredTheme = (): string => {
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  return storedTheme !== null && storedTheme in themes ? storedTheme : DEFAULT_THEME;
};

const config = getEmbedConfig();
const authService = getAuthService();

const sentryOptions =
  config !== null && config.sentry_reporting_uri
    ? {
        isEnabled: true,
        isHttpClientCaptureEnabled: true,
        dsn: config.sentry_reporting_uri,
        release: config.release,
        environment: config.sentry_environment,
        ignoreErrors: [
          /ResizeObserver loop limit exceeded/,
          /ResizeObserver loop completed with undelivered notifications/,
          // Fix by: https://app.asana.com/0/1202007030618467/1205798219208199
          /Response not successful: Received status code 401/,
          // This is a normal flow for the app, we don't want to report it.
          /Not authorized/,
        ],
        thirdPartyErrorFilter: {
          applicationKey: config.sentry_app_key ?? 'backoffice-web-appkey',
        },
      }
    : { isEnabled: false };

// Define the function externally, so its identity would remain the same across rerenders.
const getIconPath = (name: string) => `${iconsUrl}#${name}`;

const Root: React.FC = () => {
  const [currentTheme, setCurrentTheme] = useState<string>(getStoredTheme);

  const handleThemeChange = useCallback((theme: string) => {
    setCurrentTheme(theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, []);

  const children = useMemo(
    () => (
      <NotificationBoundary
        className={{
          wrapper: 'notifications',
          container: 'container',
          content: 'content',
          notifications: 'notification-content',
        }}>
        <HelmetProvider>
          <App authService={authService} />
        </HelmetProvider>
      </NotificationBoundary>
    ),
    [],
  );

  return (
    <ErrorHandlerProvider sentryOptions={sentryOptions}>
      <ErrorBoundary renderError={(error) => <ErrorPlaceholder error={error} />}>
        <ThemeProvider
          getIconPath={getIconPath}
          themes={themes}
          currentTheme={currentTheme}
          onThemeChange={handleThemeChange}>
          <ToastProvider>{children}</ToastProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </ErrorHandlerProvider>
  );
};

const root = createRoot(document.getElementById('root')!);
root.render(<Root />);
